import { Config } from '../types'
import { processDomain } from '../utils/domain'

interface KeystoneConfigResponse {
  data: Record<
    string,
    {
      domain: string
      languages: string[]
      segment: { key: string; host: string }
      oneTrust?: {
        key: string
        blockTrackersBeforeConsent?: boolean
      }
      anonymizedSegmentProperties?: string[]
      braze?: { key: string; host: string; optOutInApp?: boolean }
      brazeInAppConfiguration?: {
        allowedEvents: string[]
        allowedProperties: string[]
        eventsToTurnGeneric: string[]
        genericEventName: string
      }
      googleapi?: { key: string }
      appleAppSiteAssociation?: {
        applinks: {
          apps?: string[]
          details: { appID: string; paths?: string[] }[]
        }
      }
      storeIds: Record<string, string>
    }
  >
  createdAt: string
  version: string
}

interface KeystoneServiceConfig {
  baseUrl: string
  configKey: string
  apiKey?: string
  timeout?: number
  retryCount?: number
  retryDelay?: number
}

class KeystoneService {
  private config: KeystoneServiceConfig
  private cache: KeystoneConfigResponse | null = null
  private lastFetch = 0
  private readonly CACHE_TTL = 5 * 60 * 1000 // 5 minutes

  constructor(config: KeystoneServiceConfig) {
    this.config = {
      timeout: 10000,
      retryCount: 3,
      retryDelay: 1000,
      ...config,
    }
  }

  /**
   * Fetch configurations from Keystone service
   */
  async fetchConfigurations(): Promise<KeystoneConfigResponse> {
    const now = Date.now()

    // Return cached data if still valid
    if (this.cache && now - this.lastFetch < this.CACHE_TTL) {
      return this.cache
    }

    const url = `${this.config.baseUrl}/api/configurations/${this.config.configKey}?merged=true`
    let lastError: Error

    for (let attempt = 1; attempt <= (this.config.retryCount ?? 3); attempt++) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), this.config.timeout)

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(this.config.apiKey && {
              Authorization: `Bearer ${this.config.apiKey}`,
            }),
          },
          signal: controller.signal,
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data: KeystoneConfigResponse = await response.json()

        // Validate response structure
        if (!this.isValidConfigResponse(data)) {
          throw new Error('Invalid configuration response format')
        }

        this.cache = data
        this.lastFetch = now

        console.info('Configuration fetched successfully from Keystone', {
          version: data.version,
          countries: Object.keys(data.data).length,
        })

        return data
      } catch (error) {
        lastError = error as Error
        console.warn(
          `Keystone fetch attempt ${attempt}/${this.config.retryCount} failed:`,
          {
            error: lastError.message,
            attempt,
            willRetry: attempt < (this.config.retryCount ?? 3),
          }
        )

        if (attempt < (this.config.retryCount ?? 3)) {
          await this.delay((this.config.retryDelay ?? 1000) * attempt)
        }
      }
    }

    // If all attempts failed and we have cache, return stale data
    if (this.cache) {
      console.warn('Using stale configuration data due to fetch failures')
      return this.cache
    }

    throw lastError ?? new Error('Failed to fetch configuration after retries')
  }

  /**
   * Check if there are configuration updates available
   */
  async checkForUpdates(): Promise<{ hasUpdates: boolean; version?: string }> {
    try {
      const url = `${this.config.baseUrl}/api/configurations/${this.config.configKey}/meta`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && {
            Authorization: `Bearer ${this.config.apiKey}`,
          }),
        },
        signal: AbortSignal.timeout(5000), // Quick check
      })

      if (!response.ok) {
        return { hasUpdates: false }
      }

      const { version } = await response.json()
      const hasUpdates = !this.cache || this.cache.version !== version

      return { hasUpdates, version }
    } catch (error) {
      console.warn('Failed to check for configuration updates:', error)
      return { hasUpdates: false }
    }
  }

  /**
   * Transform Keystone response to application config format
   */
  transformToAppConfig(keystoneData: KeystoneConfigResponse): {
    config: Config
    domains: Record<string, string>
  } {
    const config: Config = {}
    const domains: Record<string, string> = {}

    // Transform country configurations
    Object.entries(keystoneData.data).forEach(([countryCode, countryConfig]) => {
      const processedDomain = processDomain({
        countryCode,
        originalDomain: countryConfig.domain,
      })

      domains[processedDomain] = countryCode
      config[countryCode] = {
        languages: countryConfig.languages,
        segment: {
          key: countryConfig.segment?.key || '',
          host: countryConfig.segment?.host || 'https://api.segment.io',
        },
        onetrust: {
          key: countryConfig.oneTrust?.key,
          blockTrackersBeforeConsent:
            countryConfig.oneTrust?.blockTrackersBeforeConsent || false,
        },
        braze: {
          key: countryConfig.braze?.key || '',
          host: countryConfig.braze?.host || 'sdk.iad-03.braze.com',
          optOutInApp: countryConfig.braze?.optOutInApp ?? true,
        },
        googleapi: countryConfig.googleapi?.key
          ? {
              key: countryConfig.googleapi.key,
            }
          : null,
        appleAppSiteAssociation: {
          applinks: {
            apps: countryConfig.appleAppSiteAssociation?.applinks.apps ?? [],
            details: countryConfig.appleAppSiteAssociation?.applinks?.details || [],
          },
        },
        anonymizedSegmentProperties: countryConfig.anonymizedSegmentProperties ?? [],
        brazeInAppConfiguration: {
          allowedEvents: countryConfig.brazeInAppConfiguration?.allowedEvents ?? [],
          allowedProperties:
            countryConfig.brazeInAppConfiguration?.allowedProperties ?? [],
          eventsToTurnGeneric:
            countryConfig.brazeInAppConfiguration?.eventsToTurnGeneric ?? [],
          genericEventName:
            countryConfig.brazeInAppConfiguration?.genericEventName ?? '',
        },
        ...(countryConfig.storeIds && Object.keys(countryConfig.storeIds).length > 0
          ? { storeIds: countryConfig.storeIds }
          : {}),
      }
    })

    return {
      config,
      domains,
    }
  }

  /**
   * Clear cache to force fresh fetch
   */
  clearCache(): void {
    this.cache = null
    this.lastFetch = 0
  }

  /**
   * Get cached data if available
   */
  getCachedData(): KeystoneConfigResponse | null {
    return this.cache
  }

  private isValidConfigResponse(data: any): data is KeystoneConfigResponse {
    return (
      data &&
      typeof data === 'object' &&
      typeof data.data === 'object' &&
      typeof data.createdAt === 'string' // &&
      //typeof data.version === 'string'
    )
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

export default KeystoneService
export type { KeystoneConfigResponse, KeystoneServiceConfig }
